import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const activeMuters = new Set();

export const VideoTile = ({ src, srcMobile, srcWebm, poster, className = "", caption, testid, allowSound = false }) => {
  const ref = useRef(null);
  const muteSelf = useRef(null);
  const [soundOn, setSoundOn] = useState(false);
  const chosenSrc = srcMobile && typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches ? srcMobile : src;

  useEffect(() => {
    if (!allowSound) return;
    const cb = () => {
      const el = ref.current;
      if (el) el.muted = true;
      setSoundOn(false);
    };
    muteSelf.current = cb;
    activeMuters.add(cb);
    return () => activeMuters.delete(cb);
  }, [allowSound]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const toggleSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    const next = !soundOn;
    if (next) activeMuters.forEach((fn) => fn !== muteSelf.current && fn());
    el.muted = !next;
    if (next) el.play().catch(() => {});
    setSoundOn(next);
  };

  return (
    <figure className={`relative overflow-hidden bg-ink m-0 ${className}`} data-testid={testid}>
      <video
        ref={ref}
        poster={poster || undefined}
        muted
        loop
        playsInline
        preload="metadata"
        className="h-full w-full object-cover"
      >
        <source src={chosenSrc} type="video/mp4" />
        {srcWebm && <source src={srcWebm} type="video/webm" />}
      </video>
      {caption && (
        <figcaption className="absolute bottom-3 left-3 font-mono-label text-white/80 bg-ink/50 backdrop-blur-sm px-2 py-1">
          {caption}
        </figcaption>
      )}
      {allowSound && (
        <button
          type="button"
          onClick={toggleSound}
          aria-label={soundOn ? "Mute tape" : "Unmute tape"}
          data-testid={testid ? `${testid}-sound` : "tape-sound-toggle"}
          className="absolute bottom-3 right-3 flex items-center gap-2 font-mono-label text-white/80 bg-ink/60 backdrop-blur-sm border border-white/20 px-2.5 py-1.5 hover:bg-white hover:text-ink transition-colors duration-200"
        >
          {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
          {soundOn ? "ON" : "SOUND"}
        </button>
      )}
    </figure>
  );
};
